'use client'

import { Tooltip } from 'shared/components/tooltip'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'

interface RelativeTimeProps {
  time: string
}

const SECOND = 1_000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY
const MONTH = 30 * DAY
const YEAR = 365 * DAY

interface CompactTimeProps {
  time: string
  relativeTime: string
  formattedDateTime: string
}

// Stacks from tablet up, where the column is too narrow for one line.
function CompactTime({ time, relativeTime, formattedDateTime }: CompactTimeProps) {
  return (
    <span className="desktop:hidden tablet:flex-col tablet:items-start flex flex-wrap items-center gap-1 text-sm">
      <time className={'text-text-tertiary'} dateTime={time}>
        {relativeTime}
      </time>
      <span className="text-icon-tertiary tablet:hidden">•</span>
      <time className={'text-text-quaternary'} dateTime={time}>
        {formattedDateTime}
      </time>
    </span>
  )
}

interface TooltipTimeProps {
  time: string
  relativeTime: string
  formattedDate: string
  formattedTime: string
}

function TooltipTime({ time, relativeTime, formattedDate, formattedTime }: TooltipTimeProps) {
  return (
    <div className="max-desktop:hidden contents">
      <Tooltip
        label={
          <span className="text-sm">
            {formattedDate} <span className="text-text-tertiary">•</span> {formattedTime}
          </span>
        }
      >
        <time dateTime={time}>{relativeTime}</time>
      </Tooltip>
    </div>
  )
}

function RelativeTime({ time }: RelativeTimeProps) {
  const { t, tICU } = useLocoTranslation()
  const { intlLocale, user } = useUser()
  const date = new Date(time)
  const elapsed = Date.now() - date.getTime()

  if (!user || Number.isNaN(date.getTime())) return null

  if (elapsed < 0) {
    const formattedFutureDate = new Intl.DateTimeFormat(intlLocale, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: user.timezone,
    }).format(date)

    return <time dateTime={time}>{formattedFutureDate}</time>
  }

  let relativeTime: string

  if (elapsed < MINUTE) {
    relativeTime = t('global.times.just_now')
  } else if (elapsed < HOUR) {
    relativeTime = tICU('global.times.minutes_ago', { count: Math.floor(elapsed / MINUTE) })
  } else if (elapsed < DAY) {
    relativeTime = tICU('global.times.hours_ago', { count: Math.floor(elapsed / HOUR) })
  } else if (elapsed < WEEK) {
    relativeTime = tICU('global.times.days_ago', { count: Math.floor(elapsed / DAY) })
  } else if (elapsed < MONTH) {
    relativeTime = tICU('global.times.weeks_ago', { count: Math.floor(elapsed / WEEK) })
  } else if (elapsed < YEAR) {
    relativeTime = tICU('global.times.months_ago', { count: Math.floor(elapsed / MONTH) })
  } else {
    relativeTime = tICU('global.times.years_ago', { count: Math.floor(elapsed / YEAR) })
  }

  const formattedDateTime = new Intl.DateTimeFormat(intlLocale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: user.timezone,
  }).format(date)
  const formattedDate = new Intl.DateTimeFormat(intlLocale, {
    dateStyle: 'long',
    timeZone: user.timezone,
  }).format(date)
  const formattedTime = new Intl.DateTimeFormat(intlLocale, {
    timeStyle: 'short',
    timeZone: user.timezone,
  }).format(date)

  // Both forms render and CSS picks one: the first-paint breakpoint comes from the
  // request User-Agent, so choosing here would repaint once hydration corrects it.
  return (
    <>
      <CompactTime time={time} relativeTime={relativeTime} formattedDateTime={formattedDateTime} />
      <TooltipTime
        time={time}
        relativeTime={relativeTime}
        formattedDate={formattedDate}
        formattedTime={formattedTime}
      />
    </>
  )
}

export { RelativeTime }
export type { RelativeTimeProps }
